import React, { useState } from 'react';
import Link from 'next/link';
import { SearchIcon, MenuIcon, ChevronDownIcon } from './Icons';

interface HeaderProps {
    onMenuClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isToolsOpen, setIsToolsOpen] = useState(false);

    return (
        <header className="sticky top-0 z-30 w-full bg-white border-b border-stone-100/80 backdrop-blur-md shadow-sm">
            <div className="w-full px-3 sm:px-4 lg:px-6 h-16 flex items-center justify-between gap-2 sm:gap-4">

                <div className="flex items-center gap-3">
                    <button
                        onClick={onMenuClick}
                        className="p-2 -ml-2 lg:hidden text-stone-600 hover:bg-stone-50 rounded-lg transition-colors"
                        aria-label="Toggle menu"
                    >
                        <MenuIcon className="w-6 h-6" />
                    </button>

                    <Link href="/" className="font-serif text-lg sm:text-xl md:text-2xl font-bold text-stone-900 tracking-tight flex-shrink-0" aria-label="Sweety's Bakery Home">
                        Sweety's <span className="text-brand-pink">Bakery</span>
                    </Link>
                </div>

                {/* Desktop Navigation Links */}
                <nav className="hidden lg:flex items-center gap-6">
                    <div 
                        className="relative"
                        onMouseEnter={() => setIsToolsOpen(true)}
                        onMouseLeave={() => setIsToolsOpen(false)}
                    >
                        <button 
                            className="flex items-center gap-1 text-sm font-bold text-stone-600 hover:text-brand-pink transition-colors py-5"
                            aria-haspopup="true"
                            aria-expanded={isToolsOpen}
                            aria-label="Toggle tools menu"
                        >
                            Tools <ChevronDownIcon className={`w-4 h-4 transition-transform ${isToolsOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isToolsOpen && (
                            <div className="absolute top-full left-0 w-48 bg-white border border-stone-100 shadow-xl rounded-xl p-2 py-3 fade-in">
                                <Link href="/tools/cake-pricing" className="block px-4 py-2 text-sm text-stone-600 hover:bg-brand-pink/5 hover:text-brand-pink rounded-lg font-medium">Pillar Calculators</Link>
                                <Link href="/tools/measurement-converter" className="block px-4 py-2 text-sm text-stone-600 hover:bg-brand-pink/5 hover:text-brand-pink rounded-lg font-medium">Converters</Link>
                                <Link href="/tools/management-dashboard" className="block px-4 py-2 text-sm text-stone-600 hover:bg-brand-pink/5 hover:text-brand-pink rounded-lg font-medium">Business Tools</Link>
                            </div>
                        )}
                    </div>
                    <Link href="/recipes" className="text-sm font-bold text-stone-600 hover:text-brand-pink transition-colors">Recipes</Link>
                    <Link href="/blog" className="text-sm font-bold text-stone-600 hover:text-brand-pink transition-colors">Blog</Link>
                    <Link href="/about" className="text-sm font-bold text-stone-600 hover:text-brand-pink transition-colors">About</Link>
                    <Link href="/saas" className="px-5 py-2 text-sm font-bold text-white bg-brand-pink rounded-full shadow-md hover:bg-brand-pink/90 hover:shadow-lg transition-all transform hover:-translate-y-0.5">
                        Bakery Pro
                    </Link>
                </nav>

                {/* Right Actions */}
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="relative hidden xl:block max-w-xs w-full">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon className="h-4 w-4 text-stone-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-stone-200 rounded-lg leading-5 bg-stone-50 text-stone-900 placeholder-stone-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-brand-pink focus:border-brand-pink sm:text-sm transition-colors"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </header>
    );
};
